import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea", "buttonsContainer"]

  connect() {
    this.setupButtons()

    // textareaの変更を監視
    this.textareaTarget.addEventListener('input', () => {
      this.setupButtons() // 位置更新ではなくボタン再作成
    })

    this.textareaTarget.addEventListener('scroll', () => {
      this.markScrolling()
      this.updateButtonPositions()
      this.scheduleScrollEnd()
    })

    // タッチ操作開始時にすぐ隠す（スクロール開始前でも慣性移動を防ぐ）
    this.textareaTarget.addEventListener('touchstart', () => {
      this.markScrolling()
    }, { passive: true })
  }

  disconnect() {
    clearTimeout(this.scrollEndTimer)
  }

  markScrolling() {
    this.buttonsContainerTarget.classList.add('is-scrolling')
  }

  scheduleScrollEnd() {
    // scrollendイベントは古いiOS Safariで未対応のため、タイマーで停止検知
    clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = setTimeout(() => {
      this.updateButtonPositions()
      this.buttonsContainerTarget.classList.remove('is-scrolling')
    }, 150)
  }

  setupButtons() {
    const text = this.textareaTarget.value
    const sections = this.findSections(text)
    
    // ボタンコンテナをクリア
    this.buttonsContainerTarget.innerHTML = ''
    
    // 各セクションにボタンを作成
    sections.forEach(section => {
      const button = this.createCopyButton(section)
      this.buttonsContainerTarget.appendChild(button)
    })
    
    this.updateButtonPositions()
  }

  findSections(text) {
    const sections = []
    const lines = text.split('\n')
    const sectionPattern = /^\(([SOAP])\)/
    
    lines.forEach((line, lineIndex) => {
      const match = line.match(sectionPattern)
      if (match) {
        const sectionType = match[1]
        
        // 次のセクションまでの内容を取得
        const nextSectionIndex = lines.findIndex((l, i) => 
          i > lineIndex && sectionPattern.test(l)
        )
        const endLineIndex = nextSectionIndex !== -1 ? nextSectionIndex : lines.length
        
        const content = lines.slice(lineIndex, endLineIndex).join('\n').trim()
        
        sections.push({
          type: sectionType,
          lineIndex: lineIndex,
          content: content
        })
      }
    })
    
    return sections
  }

  createCopyButton(section) {
    const button = document.createElement('button')
    button.className = 'absolute flex items-center justify-center w-7 h-7 text-gray-500 hover:text-gray-700 rounded transition-colors pointer-events-auto cursor-pointer'
    button.dataset.sectionType = section.type
    button.dataset.lineIndex = section.lineIndex
    button.title = `${section.type}セクションをコピー`
    
    button.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_${section.type})">
          <path d="M16.0579 13.125H8.02893C7.66094 13.125 7.35986 12.8438 7.35986 12.5V2.5C7.35986 2.15625 7.66094 1.875 8.02893 1.875H13.8875L16.7269 4.52734V12.5C16.7269 12.8438 16.4259 13.125 16.0579 13.125ZM8.02893 15H16.0579C17.534 15 18.7342 13.8789 18.7342 12.5V4.52734C18.7342 4.03125 18.5209 3.55469 18.1446 3.20312L15.3093 0.550781C14.933 0.199219 14.4228 0 13.8917 0H8.02893C6.55278 0 5.35262 1.12109 5.35262 2.5V12.5C5.35262 13.8789 6.55278 15 8.02893 15ZM2.67631 5C1.20016 5 0 6.12109 0 7.5V17.5C0 18.8789 1.20016 20 2.67631 20H10.7052C12.1814 20 13.3816 18.8789 13.3816 17.5V16.25H11.3743V17.5C11.3743 17.8438 11.0732 18.125 10.7052 18.125H2.67631C2.30832 18.125 2.00723 17.8438 2.00723 17.5V7.5C2.00723 7.15625 2.30832 6.875 2.67631 6.875H4.01447V5H2.67631Z" fill="currentColor"/>
        </g>
        <defs>
          <clipPath id="clip0_${section.type}">
            <rect width="18.7342" height="20" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    `
    
    button.addEventListener('click', (e) => {
      e.preventDefault()
      this.copySection(section.type, button)
    })
    
    return button
  }

  copySection(sectionType, button) {
    const sections = this.findSections(this.textareaTarget.value)
    const targetSection = sections.find(s => s.type === sectionType)
    
    if (targetSection) {
      navigator.clipboard.writeText(targetSection.content).then(() => {
        this.showCopyFeedback(button)
      })
    }
  }

  showCopyFeedback(button) {
    const svg = button.querySelector('svg')

    // 元のSVGを保存
    const originalSvg = svg.outerHTML

    button.classList.add('text-green-600')
    button.classList.remove('text-gray-500', 'hover:text-gray-700')

    // チェックマークアイコンに変更
    svg.outerHTML = `
      <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
        <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
      </svg>
    `

    setTimeout(() => {
      // アイコンを元に戻す
      const currentSvg = button.querySelector('svg')
      currentSvg.outerHTML = originalSvg

      button.classList.remove('text-green-600')
      button.classList.add('text-gray-500', 'hover:text-gray-700')
    }, 1500)
  }

  updateButtonPositions() {
    // SOAPセクション（(S), (O), (A), (P)）の隣にコピーボタンを正確に配置するため
    // テキストエリア内のテキスト位置を計算し、スクロール位置も考慮してボタンを配置
    const buttons = this.buttonsContainerTarget.querySelectorAll('button')
    if (buttons.length === 0) return
    
    const text = this.textareaTarget.value
    const scrollTop = this.textareaTarget.scrollTop
    
    
    buttons.forEach(button => {
      const sectionType = button.dataset.sectionType
      const sectionPattern = `(${sectionType})`
      const sectionIndex = text.indexOf(sectionPattern)
      
      if (sectionIndex !== -1) {
        const position = this.getAccurateTextPosition(sectionIndex)
        const topPosition = position.top - scrollTop - 6 // 6px上にずらす
        const leftPosition = position.left + position.width + 8 // (S)の右側に8px間隔で配置
        
        // (S)ラベルのすぐ右に配置
        button.style.top = `${topPosition}px`
        button.style.left = `${leftPosition}px`
        button.style.right = 'auto' // rightを無効化
        button.style.zIndex = '10'
        
        // 見えない範囲のボタンは非表示
        const textareaHeight = this.textareaTarget.clientHeight
        
        if (topPosition < -20 || topPosition > textareaHeight) {
          button.style.display = 'none'
        } else {
          button.style.display = 'flex'
        }
      }
    })
  }

  getAccurateTextPosition(charIndex) {
    let virtualDiv = null
    
    try {
      virtualDiv = this.createVirtualDiv(charIndex)
      return this.calculateRelativePosition(virtualDiv)
    } catch (error) {
      return { top: 0, left: 0, width: 0 }
    } finally {
      this.cleanupVirtualDiv(virtualDiv)
    }
  }

  createVirtualDiv(charIndex) {
    const virtualDiv = document.createElement('div')
    this.copyTextareaStyles(virtualDiv)
    
    // 指定位置までのテキストを挿入
    const textBefore = this.textareaTarget.value.substring(0, charIndex)
    virtualDiv.textContent = textBefore
    
    // セクションパターンをspan要素で追加
    const sectionSpan = this.createSectionSpan(charIndex)
    virtualDiv.appendChild(sectionSpan)
    
    // DOMに追加して配置
    document.body.appendChild(virtualDiv)
    this.positionVirtualDiv(virtualDiv)
    
    return virtualDiv
  }

  createSectionSpan(charIndex) {
    const sectionPattern = this.textareaTarget.value.substring(charIndex, charIndex + 3)
    const span = document.createElement('span')
    span.textContent = sectionPattern
    return span
  }

  positionVirtualDiv(virtualDiv) {
    const textareaRect = this.textareaTarget.getBoundingClientRect()
    virtualDiv.style.left = textareaRect.left + 'px'
    virtualDiv.style.top = textareaRect.top + 'px'
  }

  calculateRelativePosition(virtualDiv) {
    const span = virtualDiv.querySelector('span')
    const rect = span.getBoundingClientRect()
    const textareaRect = this.textareaTarget.getBoundingClientRect()
    
    return {
      top: rect.top - textareaRect.top,
      left: rect.left - textareaRect.left,
      width: rect.width
    }
  }

  cleanupVirtualDiv(virtualDiv) {
    if (virtualDiv && virtualDiv.parentNode) {
      virtualDiv.parentNode.removeChild(virtualDiv)
    }
  }

  copyTextareaStyles(element) {
    const computedStyle = window.getComputedStyle(this.textareaTarget)
    
    // 重要なスタイルをコピー
    element.style.position = 'absolute'
    element.style.visibility = 'hidden'
    element.style.whiteSpace = 'pre-wrap'
    element.style.wordWrap = 'break-word'
    element.style.overflow = 'hidden'
    element.style.fontFamily = computedStyle.fontFamily
    element.style.fontSize = computedStyle.fontSize
    element.style.lineHeight = computedStyle.lineHeight
    element.style.padding = computedStyle.padding
    element.style.border = computedStyle.border
    element.style.margin = '0'
    element.style.width = this.textareaTarget.clientWidth + 'px'
    element.style.boxSizing = computedStyle.boxSizing
    element.style.zIndex = '-9999'
  }
}